import type { OrderLine } from '@/types/Checkout';
import type {
  OrderM3Response,
  OrderRequest,
  OrderRequestLine,
  FreightResponse,
  FreightResponseFailed,
  PackagingResponse,
  PackagingResponseFailed,
  RowMeasurements,
  OrderFreightData,
} from './freight.d';
import { ListItem } from '@/types/Productlist';
import { rowsToOrderlines } from '@/helpers/checkout';
import { instance, cancel, cancelRequest } from '@/data/api/freight/axios';
import { LineKey } from '@/helpers/linekey';

export { cancelRequest };

function mapOrderLines(rows: ListItem[], rowMeasurements?: RowMeasurements): OrderRequestLine[] {
  return rowsToOrderlines(rows)
    .map((item: OrderLine) => {
      if (!item.lineNumber) {
        // Might be a bad way to handle this edge case, but generally it should not happen
        console.warn('lineNumber is missing', item);
        item.lineNumber = '';
      }
      // FreightFailedProductlist only has row numbers to go off of, only match against those
      const lineKey = new LineKey({ lineNumber: item.lineNumber!, lineNumberPosition: item.lineNumberPosition });
      return {
        lineNumber: item.lineNumber,
        lineNumberPosition: item.lineNumberPosition,
        jointDeliveryCode: item.jointDeliveryCode,
        itemNumber: item.itemNumber,
        unit: item.salesUnit,
        quantity: item.salesUnitQuantity,
        warehouse: item.warehouse,
        configurationItem: item.configurationItem,
        userDefinedHeight: rowMeasurements?.[lineKey.toString()]?.height,
        userDefinedLength: rowMeasurements?.[lineKey.toString()]?.length,
        userDefinedWidth: rowMeasurements?.[lineKey.toString()]?.width,
        userDefinedWeight: rowMeasurements?.[lineKey.toString()]?.weight,
      } as OrderRequestLine;
    })
    .filter((item) => Object.keys(item).length > 0);
}

export function getOrderFreight(this: any, data: OrderFreightData): Promise<FreightResponse | FreightResponseFailed> {
  const orderLines = mapOrderLines(data.rows, data.rowMeasurements);
  const request: OrderRequest = {
    referenceNumber: data.referenceNumber,
    orderNumber: data.orderNumber,
    customerNumber: data.customerNumber,
    deliveryCountryCode: data.deliveryCountryCode,
    deliveryPostalCode: data.deliveryPostalCode,
    isNoLimitTransport: data.isNoLimitTransport,
    statisticsIdentity1Customer: data.statisticsIdentity1Customer,
    addressId: data.addressId,
    division: data.division,
    orderType: data.orderType,
    orderLines,
  };

  // Do Request
  const _uid = this?._uid ?? '';
  cancel[`freight${_uid}`] = new AbortController();
  return new Promise((resolve, reject) => {
    instance
      .post(`freight`, request, {
        signal: cancel[`freight${_uid}`].signal,
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          reject('abort');
        } else if (error.response?.status === 409) {
          // Not an actual error,
          // we just need more information to complete the request
          const data = error.response?.data as FreightResponseFailed;
          resolve(data);
        } else {
          reject(error);
        }
      })
      .then((response) => {
        if (response?.status === 200) {
          const data = response.data as FreightResponse;
          resolve(data);
        } else {
          reject(response);
        }
      });
  });
}

export function getOrderPackaging(
  this: any,
  data: OrderFreightData
): Promise<PackagingResponse | PackagingResponseFailed> {
  const orderLines = mapOrderLines(data.rows);
  const request: OrderRequest = {
    referenceNumber: data.referenceNumber,
    orderNumber: data.orderNumber,
    customerNumber: data.customerNumber,
    deliveryCountryCode: data.deliveryCountryCode,
    deliveryPostalCode: data.deliveryPostalCode,
    division: data.division,
    orderLines,
  };

  // Do Request
  const _uid = this?._uid ?? '';
  cancel[`packaging${_uid}`] = new AbortController();
  return new Promise((resolve, reject) => {
    instance
      .post(`packaging`, request, {
        signal: cancel[`packaging${_uid}`].signal,
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          reject('abort');
        } else {
          reject(error);
        }
      })
      .then((response) => {
        if (response?.status === 200) {
          const data = response.data as PackagingResponse;
          resolve(data);
        } else if (response?.status === 409) {
          const data = response.data as PackagingResponseFailed;
          resolve(data);
        } else {
          reject(response);
        }
      });
  });
}

export function getOrderM3Async(
  orderNumber: string,
  guid?: string
): Promise<OrderM3Response & { result: () => Promise<OrderM3Response> }> {
  const cancelKey = `ordersM3${guid ?? orderNumber}`;
  cancel[cancelKey] = new AbortController();
  return new Promise((resolve, reject) => {
    if (!orderNumber) {
      reject('Order number is missing');
      return;
    }

    instance
      .get(`get/${orderNumber}/async`, {
        params: {
          guid,
        },
        signal: cancel[cancelKey].signal,
      })
      .catch((error) => {
        reject(error);
      })
      .then((response) => {
        if (response?.status === 200) {
          const data = response.data as OrderM3Response;
          resolve({
            ...data,
            result: async () => {
              return new Promise(async (resolve, reject) => {
                if (!orderNumber) {
                  reject('Order number is missing');
                  return;
                }

                // Poll for results
                while (true) {
                  if (cancel[cancelKey].signal.aborted) {
                    reject('Request cancelled');
                    return;
                  }

                  const response = await instance
                    .get(`get/${orderNumber}/result`, {
                      params: {
                        guid,
                      },
                      signal: cancel[cancelKey].signal,
                    })
                    .catch((error) => {
                      reject(error);
                      return;
                    });

                  if (!response) return;

                  if (response?.status === 200) {
                    const data = response.data as OrderM3Response;
                    resolve(data);
                    return;
                  } else if (response?.status === 202) {
                    await new Promise((r) => setTimeout(r, 2000));
                  } else {
                    reject(response);
                    return;
                  }
                }
              });
            },
          });
        } else {
          reject(response);
        }
      });
  });
}

export function getOrderM3(orderNumber: string, guid?: string): Promise<OrderM3Response> {
  const cancelKey = `ordersM3${guid ?? orderNumber}`;
  cancel[cancelKey] = new AbortController();
  return new Promise((resolve, reject) => {
    if (!orderNumber) {
      reject('Order number is missing');
      return;
    }

    instance
      .get(`get/${orderNumber}`, {
        params: {
          guid,
        },
        signal: cancel[cancelKey].signal,
      })
      .catch((error) => {
        reject(error);
      })
      .then((response) => {
        if (response?.status === 200) {
          const data = response.data as OrderM3Response;
          resolve(data);
        } else {
          reject(response);
        }
      });
  });
}

export function unlockOrderM3(orderNumber: string, guid?: string): Promise<boolean | any> {
  cancelRequest(`ordersM3${guid ?? orderNumber}`); // Cancel existing request
  return new Promise((resolve, reject) => {
    if (!orderNumber) {
      reject('Order number is missing');
      return;
    }

    instance
      .post(`unlock/${orderNumber}`, null, {
        params: {
          guid,
        },
      })
      .catch((error) => {
        reject(error);
      })
      .then((response) => {
        if (response?.status === 200 && response?.data?.success === true) {
          resolve(true);
        } else {
          reject(response);
        }
      });
  });
}
